import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_FORM_URL = "https://formspree.io/f/xgoqdank";

const subjectOptions = [
  "Kozijnreparatie",
  "Schuifpui reparatie",
  "Draaikiepraam reparatie",
  "Glas vervangen HR++",
  "Slot- en cilindervervanging",
  "Velux dakraam reparatie",
  "Inbraakschade reparatie",
  "Raamkruk vervangen",
  "Anders / advies",
];

export default function OfferteForm() {
  const { t } = useTranslation("home");
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    naam: "",
    email: "",
    telefoon: "",
    onderwerp: "",
    bericht: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    try {
      const res = await fetch(DEFAULT_FORM_URL, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
      }
    } catch {
      /* network error */
    }
  };

  const contactInfo = [
    {
      icon: "ri-phone-line",
      label: "Telefoon",
      value: "040 798 7470",
      href: "tel:0407987470",
    },
    {
      icon: "ri-mail-line",
      label: "E-mail",
      value: "info@ramen-repair-eindhoven.nl",
      href: "mailto:info@ramen-repair-eindhoven.nl",
    },
    {
      icon: "ri-time-line",
      label: "Bereikbaar",
      value: "Ma — Zo: 08:00 – 20:00",
    },
    {
      icon: "ri-map-pin-line",
      label: "Werkgebied",
      value: "Eindhoven & omstreken",
    },
  ];

  return (
    <section id="offerte" className="bg-brand-cream py-20 md:py-[120px] w-full relative overflow-hidden">
      {/* Decorative background shape */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-yellow/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-blue/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

      <div className="max-w-[1200px] mx-auto px-4 md:px-10 relative z-10">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <h2 className="font-geist font-bold text-3xl md:text-[48px] text-brand-text mb-4">
            Neem contact op
          </h2>
          <p className="font-geist text-base md:text-lg text-brand-text/60 max-w-[600px] mx-auto">
            Heeft u een vraag of wilt u een gratis offerte aanvragen? Vul het formulier in en wij reageren binnen 24 uur.
          </p>
        </div>

        {submitted ? (
          <div className="max-w-[600px] mx-auto text-center py-16 bg-white rounded-3xl border border-brand-border">
            <div className="w-20 h-20 rounded-full bg-brand-yellow flex items-center justify-center mx-auto mb-6 animate-bounce">
              <i className="ri-check-line w-10 h-10 flex items-center justify-center text-brand-text text-3xl"></i>
            </div>
            <h3 className="font-geist font-bold text-2xl md:text-3xl text-brand-text mb-3">
              Bedankt!
            </h3>
            <p className="font-geist text-base text-brand-text/70 max-w-[400px] mx-auto">
              Wij hebben uw bericht ontvangen en nemen binnen 24 uur contact met u op.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 bg-white rounded-3xl border border-brand-border overflow-hidden">
            {/* Left: Contact info */}
            <div className="lg:col-span-5 bg-[#0C2740] p-8 md:p-12 flex flex-col justify-between relative overflow-hidden">
              {/* Decorative circles */}
              <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full border border-white/10" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full border border-white/10" />

              <div className="relative z-10">
                <h3 className="font-geist font-bold text-2xl text-white mb-2">
                  Direct contact
                </h3>
                <p className="font-geist text-sm text-white/60 mb-10">
                  Liever direct bellen? Wij zijn 7 dagen per week bereikbaar.
                </p>

                <div className="flex flex-col gap-6">
                  {contactInfo.map((item) => (
                    <div key={item.label} className="group">
                      {item.href ? (
                        <a
                          href={item.href}
                          className="flex items-start gap-4 transition-all duration-300 hover:translate-x-1"
                        >
                          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0 transition-colors duration-300 group-hover:bg-brand-yellow">
                            <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-white text-lg group-hover:text-brand-text`}></i>
                          </div>
                          <div>
                            <p className="font-geist text-xs text-white/50 uppercase tracking-wider mb-0.5">
                              {item.label}
                            </p>
                            <p className="font-geist font-bold text-base text-white">
                              {item.value}
                            </p>
                          </div>
                        </a>
                      ) : (
                        <div className="flex items-start gap-4">
                          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                            <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-white text-lg`}></i>
                          </div>
                          <div>
                            <p className="font-geist text-xs text-white/50 uppercase tracking-wider mb-0.5">
                              {item.label}
                            </p>
                            <p className="font-geist font-bold text-base text-white">
                              {item.value}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom badge */}
              <div className="relative z-10 mt-10 pt-8 border-t border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-yellow flex items-center justify-center">
                    <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center text-brand-text text-lg"></i>
                  </div>
                  <div>
                    <p className="font-geist font-bold text-sm text-white">10 jaar garantie</p>
                    <p className="font-geist text-xs text-white/50">Op alle werken en onderdelen</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-7 p-8 md:p-12">
              <form
                data-readdy-form
                id="contact-form-homepage"
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
              >
                <input
                  type="text"
                  name="_gotcha"
                  style={{ display: "none" }}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                {/* Name + Email row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="font-geist text-xs font-bold text-brand-text/70 uppercase tracking-wider block mb-2">
                      Naam
                    </label>
                    <div className="relative">
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center pointer-events-none">
                        <i className="ri-user-line w-5 h-5 flex items-center justify-center text-brand-text/30 text-lg"></i>
                      </div>
                      <input
                        type="text"
                        name="naam"
                        required
                        value={formData.naam}
                        onChange={handleChange}
                        onFocus={() => setFocusedField("naam")}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Uw naam"
                        className="w-full font-geist text-base text-brand-text bg-brand-cream/50 rounded-xl border-2 border-brand-border py-3.5 pl-10 pr-4 outline-none transition-all duration-300 focus:border-brand-yellow focus:bg-white"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="font-geist text-xs font-bold text-brand-text/70 uppercase tracking-wider block mb-2">
                      E-mail
                    </label>
                    <div className="relative">
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center pointer-events-none">
                        <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-brand-text/30 text-lg"></i>
                      </div>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField("email")}
                        onBlur={() => setFocusedField(null)}
                        placeholder="uw@email.nl"
                        className="w-full font-geist text-base text-brand-text bg-brand-cream/50 rounded-xl border-2 border-brand-border py-3.5 pl-10 pr-4 outline-none transition-all duration-300 focus:border-brand-yellow focus:bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Phone + Subject row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="font-geist text-xs font-bold text-brand-text/70 uppercase tracking-wider block mb-2">
                      Telefoon
                    </label>
                    <div className="relative">
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center pointer-events-none">
                        <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-brand-text/30 text-lg"></i>
                      </div>
                      <input
                        type="tel"
                        name="telefoon"
                        required
                        value={formData.telefoon}
                        onChange={handleChange}
                        onFocus={() => setFocusedField("telefoon")}
                        onBlur={() => setFocusedField(null)}
                        placeholder="06 123 456 78"
                        className="w-full font-geist text-base text-brand-text bg-brand-cream/50 rounded-xl border-2 border-brand-border py-3.5 pl-10 pr-4 outline-none transition-all duration-300 focus:border-brand-yellow focus:bg-white"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="font-geist text-xs font-bold text-brand-text/70 uppercase tracking-wider block mb-2">
                      Onderwerp
                    </label>
                    <div className="relative">
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center pointer-events-none">
                        <i className="ri-folder-line w-5 h-5 flex items-center justify-center text-brand-text/30 text-lg"></i>
                      </div>
                      <select
                        name="onderwerp"
                        required
                        value={formData.onderwerp}
                        onChange={handleChange}
                        onFocus={() => setFocusedField("onderwerp")}
                        onBlur={() => setFocusedField(null)}
                        className="w-full font-geist text-base text-brand-text bg-brand-cream/50 rounded-xl border-2 border-brand-border py-3.5 pl-10 pr-4 outline-none transition-all duration-300 focus:border-brand-yellow focus:bg-white appearance-none cursor-pointer"
                      >
                        <option value="" disabled>
                          Kies een onderwerp
                        </option>
                        {subjectOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center pointer-events-none">
                        <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-brand-text/30 text-lg"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="relative">
                  <label className="font-geist text-xs font-bold text-brand-text/70 uppercase tracking-wider block mb-2">
                    Bericht
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-3.5 w-10 h-10 flex items-center justify-center pointer-events-none">
                      <i className="ri-message-3-line w-5 h-5 flex items-center justify-center text-brand-text/30 text-lg"></i>
                    </div>
                    <textarea
                      name="bericht"
                      required
                      maxLength={500}
                      rows={5}
                      value={formData.bericht}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("bericht")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Beschrijf uw vraag of probleem..."
                      className="w-full font-geist text-base text-brand-text bg-brand-cream/50 rounded-xl border-2 border-brand-border py-3.5 pl-10 pr-4 outline-none transition-all duration-300 focus:border-brand-yellow focus:bg-white resize-none"
                    ></textarea>
                  </div>
                  <p className="font-geist text-xs text-brand-text/40 mt-1.5 text-right">
                    {formData.bericht.length}/500
                  </p>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="group relative w-full font-geist font-bold text-base bg-brand-yellow text-brand-text py-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-brand-yellow/30 hover:-translate-y-0.5 overflow-hidden whitespace-nowrap"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Bericht versturen
                    <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center text-lg transition-transform duration-300 group-hover:translate-x-1"></i>
                  </span>
                  <div className="absolute inset-0 bg-brand-text/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </button>

                {/* Privacy note */}
                <p className="font-geist text-xs text-brand-text/40 text-center">
                  Door te versturen gaat u akkoord met onze{" "}
                  <a href="/privacybeleid/" className="underline hover:text-brand-text transition-colors">
                    privacyverklaring
                  </a>
                  .
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}